import React, { useMemo, useState, useRef } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import { HighlightOffOutlined as HighlightOffOutlinedIcon } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";

import useStyles from "./styles";
import { useAppState } from "Context/StateContext";
import { AutoSuggest, Button, Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import { Timezone } from "Models/timezone";
import { Status } from "Util/enums";
import LocationModal, { LocationModalRef } from "./LocationModal";
import { Location } from "Models/location";
import MapInput from "Components/Form/MapInput";
import { Contact } from "Models/contact";
import GridFooter from "Components/AppGrid/shipeezi-new-grid/GridFooter";

type Props = {
  locations: Location[];
  contacts: Contact[];
  viewOnly?: boolean;
  onChange?: (location: Location[]) => void;
  timezones?: Timezone[];
  showEditButton?: boolean;
};

const ListLocation = (props: Props) => {
  const { locations, contacts, viewOnly, showEditButton, onChange } = props;
  const { currentUser } = useAppState();

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const locationModalRef = useRef<LocationModalRef>(null);
  const [indexLocationSelected, setIndexLocationSelected] = useState<number>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const handleChange = (index: number, locationPartial: Location) => {
    const locationsUpdated = [...(locations || [])];

    if (locationsUpdated && locationsUpdated.length > 0 && locationsUpdated[index]) {
      locationsUpdated[index] = {
        ...locationsUpdated[index],
        ...locationPartial,
        companyId: currentUser?.companyId,
      };
      onChange?.(locationsUpdated);
    }
  };

  const handleClose = (location?: Location) => {
    if (location) {
      if (!locations || locations?.length === 0) {
        location = { ...location, isPrimary: true };
      }
      if (indexLocationSelected !== undefined) {
        handleChange(indexLocationSelected, location);
      } else {
        const locationsUpdated = [...(locations || [])];

        locationsUpdated.push(location);
        if (onChange) onChange(locationsUpdated);
      }
    }
    setIndexLocationSelected(undefined);
  };

  const onEdit = (location: Location, index: number) => {
    setIndexLocationSelected(index);
    locationModalRef.current?.open(location);
  };

  const onAddNew = () => {
    setIndexLocationSelected(undefined);
    locationModalRef.current?.open();
  };

  const onDelete = (location: Location, index: number) => {
    if (location?.id) {
      handleChange(index, { ...location, valid: false });
    } else {
      const locationsUpdated = [...(locations || [])];
      locationsUpdated.splice(index, 1);
      onChange?.(locationsUpdated);
    }
  };

  const renderDeleteButton = (location: Location, index: number) => {
    if (location?.isPrimary || viewOnly) return null;

    return (
      <IconButton
        disabled={viewOnly}
        className={classes.iconButton}
        onClick={() => onDelete(location, index)}
      >
        <HighlightOffOutlinedIcon className={classes.icon} htmlColor="#F97066" />
      </IconButton>
    );
  };

  const renderEditButton = (location: Location, index: number) => {
    if (!showEditButton || viewOnly) return;

    return (
      <IconButton
        disabled={viewOnly}
        className={classes.iconButton}
        onClick={() => onEdit(location, index)}
      >
        <FiEdit className={classes.icon} color="#98A2B3" />
      </IconButton>
    );
  };

  const paginatedLocations = useMemo(
    () =>
      locations
        ?.filter((location) => location.valid)
        ?.sort((locationA, locationB) => {
          if (locationA?.isPrimary === locationB?.isPrimary) return 0;
          if (locationA?.isPrimary) return -1;

          return 1;
        })
        ?.slice((page - 1) * pageSize, page * pageSize),
    [locations, pageSize, page]
  );

  const renderLocations = () => {
    if (!locations?.length) return null;

    return (
      <>
        {paginatedLocations?.map((location, index) => {
          const globalIndex = (page - 1) * pageSize + index;

          return (
            <Grid item xs={12} sm={6} key={index}>
              <div className={classes.locations}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Input
                      label={translate("locationName")}
                      onChange={(event) => handleChange(globalIndex, { name: event.target.value })}
                      placeholder={translate("enterLocationName")}
                      required
                      value={location?.name}
                      disabled={viewOnly}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <AutoSuggest
                      label={translate("contactPerson")}
                      placeholder={translate("selectAContact")}
                      options={contacts}
                      data-cy="consultant"
                      inputValue={
                        location?.contact?.firstName
                          ? `${location?.contact?.firstName} ${location?.contact?.lastName}`
                          : ""
                      }
                      onChange={(consultant: Contact) => {
                        handleChange(globalIndex, {
                          contact: consultant,
                          contactId: consultant.id,
                        });
                      }}
                      getOptionLabel={(consultant?: Contact) => {
                        const name = consultant?.firstName || "";
                        const lastName = consultant?.lastName || "";

                        return `${name} ${lastName}`;
                      }}
                      getOptionSelected={(option: Contact, value: Contact) =>
                        option?.id === value?.id
                      }
                      getOptionKey={(option) => {
                        return option?.id || 0;
                      }}
                      required
                      disabled={viewOnly}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Select
                      name="agent.status"
                      label={translate("status")}
                      onChange={(event: SelectChangeEvent<any>) =>
                        handleChange(globalIndex, { status: event.target.value })
                      }
                      value={location?.status}
                      data-cy="status"
                      required
                      disabled={viewOnly}
                    >
                      <MenuItem value={Status.ACTIVE}>{translate("active")}</MenuItem>
                      <MenuItem value={Status.INACTIVE}>{translate("inactive")}</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <MapInput
                      location={location as Location}
                      onChange={(value) => {
                        handleChange(globalIndex, { ...(value as Location) });
                      }}
                      label={translate("address")}
                      inputValue={location?.longAddress}
                      placeholder={translate("startTypingToSearchDots")}
                      required
                      disabled={viewOnly}
                    />
                  </Grid>
                  {paginatedLocations.length > 1 && !viewOnly && (
                    <Grid item display="flex" justifyContent="flex-end" xs={12}>
                      {renderEditButton(location, globalIndex)}
                      {renderDeleteButton(location, globalIndex)}
                    </Grid>
                  )}
                </Grid>
              </div>
            </Grid>
          );
        })}
      </>
    );
  };

  return (
    <>
      <LocationModal ref={locationModalRef} handleClose={handleClose} contacts={contacts} />
      <Grid container direction="row" spacing={2}>
        {renderLocations()}

        {locations?.length > 6 && (
          <Grid item xs={12}>
            <GridFooter
              currentPage={page}
              pageSize={pageSize}
              totalItems={locations?.filter((loc) => loc.valid).length || 0}
              namePage={translate("locations")}
              showFooterPageInfo={true}
              handleChangePage={(option: string, newPage: number) => {
                setPage(newPage);
              }}
              onChangePageSize={(newSize: number) => {
                setPageSize(newSize);
                setPage(1);
              }}
              onGoToPage={(page: number) => setPage(page)}
            />
          </Grid>
        )}
        {!viewOnly && (
          <Grid item xs={12}>
            <Button
              data-cy="add-contact-button"
              customColor={Color.GRAY_600}
              onClick={onAddNew}
              startIcon={<AddIcon htmlColor="#FFF" />}
            >
              {translate("addNewLocation")}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ListLocation;
